import type { ReactNode } from 'react';
import { IonSegment, IonSegmentButton } from '@ionic/react';
import { cn, haptic } from '@shared/utils';

export interface SegmentedControlOption<T extends string> {
  value: T;
  label: ReactNode;
  /** 停用單一選項，例如沒有持股時的「賣出」 */
  disabled?: boolean;
}

export interface SegmentedControlProps<T extends string> {
  value: T;
  options: readonly SegmentedControlOption<T>[];
  onChange: (value: T) => void;
  /** 沒有可見標籤時必填，螢幕閱讀器靠它說明這組選項在選什麼 */
  ariaLabel?: string;
  disabled?: boolean;
  /** 傳 false 關掉左右滑動切換，用在附近還有其他橫向手勢的地方 */
  swipeGesture?: boolean;
  className?: string;
}

/**
 * 分段控制：二到四個互斥選項並排，選中的那個一眼可見。
 *
 * 外觀是 `@mobile/theme` 的 `app-segment`。`onChange` 只回傳 `options` 裡有的值，
 * 呼叫端不必再自己轉型或檢查 Ionic 事件傳回的值。
 */
export function SegmentedControl<T extends string>({
  value,
  options,
  onChange,
  ariaLabel,
  disabled,
  swipeGesture,
  className,
}: SegmentedControlProps<T>) {
  return (
    <IonSegment
      value={value}
      onIonChange={(event) => {
        const next = options.find(
          (option) => option.value === event.detail.value,
        );
        if (!next || next.value === value) return;

        haptic('tap');
        onChange(next.value);
      }}
      disabled={disabled}
      swipeGesture={swipeGesture}
      aria-label={ariaLabel}
      className={cn('app-segment', className)}
    >
      {options.map((option) => (
        <IonSegmentButton
          key={option.value}
          value={option.value}
          disabled={option.disabled}
        >
          {option.label}
        </IonSegmentButton>
      ))}
    </IonSegment>
  );
}
